import { atom, useAtomValue, type createStore, useAtom } from "jotai";
import { useEffect, useRef } from "react";
import type { RemoteName } from "../../mfe/mfeConfig";
import type { MfeRouteConfig } from "./types";
import { matchPathToMfe } from "./utils";

export const prevLocationAtom = atom<Location | null>(null);
export const locationAtom = atom<Location, [Location], void>(
  window.location,
  (get, set, newLocation) => {
    set(prevLocationAtom, { ...get(locationAtom) });
    set(locationAtom, { ...newLocation });
  }
);

export type CurrentMfe = MfeRouteConfig & {
  /** Current path used to match this mfe config.  */
  path: string;
};
export const currentMfeAtom = atom<CurrentMfe | null>(null);

export const useLocation = () => useAtomValue(locationAtom);

/**
 * Keeps location up to date by listening to popstate events generated by history.push (see shell's index file)
 */
export const syncLocation = (store: ReturnType<typeof createStore>) => {
  const updateLocation = () => store.set(locationAtom, { ...window.location });
  window.addEventListener("popstate", updateLocation);
};

/**
 * Sync current mfe state to location
 */
export const useCurrentMfe = (
  mfesRouteConfig: Record<RemoteName, MfeRouteConfig>
): CurrentMfe | null => {
  const previousMatchName = useRef<string>();
  const location = useLocation();
  const [currentMfe, setCurrentMfe] = useAtom(currentMfeAtom);

  useEffect(() => {
    const pathname = location.pathname;
    const mfeMatch = matchPathToMfe(pathname, mfesRouteConfig);

    if (!mfeMatch) {
      throw new Error(
        "No match found! make sure you've added a Fallback to the configuration"
      );
    }

    if (
      !previousMatchName.current ||
      mfeMatch.name !== previousMatchName.current
    ) {
      setCurrentMfe({ ...mfeMatch });
    }

    previousMatchName.current = mfeMatch.name;
  }, [location.pathname, mfesRouteConfig, setCurrentMfe]);

  // Checks if the previous MFE exists and is still present in the config
  const loading =
    !previousMatchName.current ||
    !(previousMatchName.current in mfesRouteConfig);

  return loading ? null : currentMfe;
};
